import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MousePointer2 } from "lucide-react";
import { HERO_BG } from "@/data/content";
import { scrollToId, EASE } from "@/components/Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-[130%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/55 to-zinc-950" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 60%, rgba(46,143,212,0.14) 0%, rgba(5,12,26,0) 70%)",
          }}
        />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-20"
      >
        <div className="overflow-hidden mb-8">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-primary font-semibold [text-shadow:0_1px_10px_rgba(5,12,26,0.95)]"
            data-testid="hero-overline"
          >
            HR99 Global Services · Recruitment & HR Consulting
          </motion.p>
        </div>

        <h1
          className="font-heading text-5xl sm:text-6xl lg:text-8xl font-medium tracking-tighter leading-[1.02] text-white max-w-5xl"
          data-testid="hero-heading"
        >
          <MaskedLine delay={0.5}>Hire top talent</MaskedLine>
          <MaskedLine delay={0.65}>
            in just <span className="text-primary">2 weeks.</span>
          </MaskedLine>
        </h1>

        <div className="overflow-hidden mt-8 max-w-xl">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="text-base sm:text-lg leading-relaxed text-zinc-400"
            data-testid="hero-subtext"
          >
            Streamline your recruitment with AI-driven precision. One single
            solution — from fresher to experienced hiring, across 400+ cities.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#contact")}
            className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-semibold text-base hover:bg-orange-400 transition-colors duration-300"
            data-testid="hero-cta-hire"
          >
            Start Hiring
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("#services")}
            className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-4 font-semibold text-base hover:border-primary hover:text-primary transition-colors duration-300"
            data-testid="hero-cta-services"
          >
            Explore Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.25em]"
        data-testid="hero-scroll-hint"
      >
        <MousePointer2 className="w-3.5 h-3.5" /> Scroll
      </motion.div>
    </section>
  );
}
